import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { useCompetitions } from './CompetitionContext';

const CreatePost = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // States dla parametrów z ReportParams
  const [reportType, setReportType] = useState('round_preview');
  const [timeRange, setTimeRange] = useState('next_24h');
  const [fromDate, setFromDate] = useState(new Date('2025-08-01T01:00:00'));
  const [toDate, setToDate] = useState(new Date());
  const [outputFormat, setOutputFormat] = useState(['facebook_post', 'x_post']);
  const [tone, setTone] = useState('analytical');
  const [competitionId, setCompetitionId] = useState(null);
  const [specialTheme, setSpecialTheme] = useState('');

  const { competitions: competitionsByLevel } = useCompetitions();

  // Opcje stale
  const reportTypeOptions = ['round_preview', 'round_summary', 'day_summary', 'match_summary'];
  const timeRangeOptions = ['last_4_days', 'last_3_hours', 'last_16_hours', 'next_7_days', 'next_24h', 'custom'];
  const outputFormatOptions = ['facebook_post', 'x_post'];
  const toneOptions = ['enthusiastic', 'neutral', 'analytical', 'humorous'];

  const competitions = useMemo(() => (
    Object.values(competitionsByLevel || {}).flat().map((comp) => ({
      id: comp.competition_id,
      name: comp.group && comp.group !== ' ' ? `${comp.name} - ${comp.group}` : comp.name,
    }))
  ), [competitionsByLevel]);
  const handleOutputFormatChange = (event) => {
    const value = event.target.value;
    setOutputFormat(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSubmit = () => {
    if (!competitionId) {
      setError('Wybierz ligę!');
      return;
    }
    if (timeRange === 'custom' && fromDate > toDate) {
      setError('Data początkowa musi być przed datą końcową!');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    const params = {
      reportType,
      timeRange,
      fromDate: timeRange === 'custom' ? fromDate.toISOString() : null,
      toDate: timeRange === 'custom' ? toDate.toISOString() : null,
      outputFormat,
      tone,
      competitionId,
      matchId: null,
      teamId: 0,
      specialTheme,
    };

    console.log('Wysyłane dane:', params); // Debugowanie danych wysyłanych
    axios
	  .post(
		`${process.env.REACT_APP_API_URL}/api/generateAndSendReport`,
		params,
		{
		  headers: {
			'Content-Type': 'application/json',
			'X-API-Key': process.env.REACT_APP_MAKE_API_KEY
		  }
		}
	  )
	  .then(() => {
		setSuccess('Raport wygenerowany i wysłany do webhooka!');
		setLoading(false);
	  })
	  .catch((err) => {
		setError('Błąd: ' + (err.response?.data?.error || err.message));
		setLoading(false);
	  });
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4" gutterBottom>
        Tworzenie posta na social media
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Typ raportu</InputLabel>
            <Select value={reportType} onChange={(e) => setReportType(e.target.value)}>
              {reportTypeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Zakres czasu</InputLabel>
            <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
              {timeRangeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {timeRange === 'custom' && (
          <>
            <Grid item xs={12} md={6}>
              <DatePicker
                selected={fromDate}
                onChange={(date) => setFromDate(date)}
                customInput={<TextField label="Od daty i godziny" fullWidth margin="normal" />}
                dateFormat="dd/MM/yyyy HH:mm"
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                popperClassName="date-picker-popper"
                portalId="date-picker-portal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                selected={toDate}
                onChange={(date) => setToDate(date)}
                customInput={<TextField label="Do daty i godziny" fullWidth margin="normal" />}
                dateFormat="dd/MM/yyyy HH:mm"
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                popperClassName="date-picker-popper"
                portalId="date-picker-portal"
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Format wyjściowy</InputLabel>
            <Select
              multiple
              value={outputFormat}
              onChange={handleOutputFormatChange}
              renderValue={(selected) => selected.join(', ')}
            >
              {outputFormatOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox checked={outputFormat.indexOf(opt) > -1} />
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Ton</InputLabel>
            <Select value={tone} onChange={(e) => setTone(e.target.value)}>
              {toneOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Liga</InputLabel>
            <Select value={competitionId} onChange={(e) => setCompetitionId(e.target.value)}>
              <MenuItem value={null}>Wybierz ligę</MenuItem>
              {Array.isArray(competitions) && competitions.length > 0 ? (
                competitions.map((comp) => (
                  <MenuItem key={comp.id} value={comp.id}>
                    {comp.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>Brak dostępnych lig</MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Specjalny temat"
            value={specialTheme}
            onChange={(e) => setSpecialTheme(e.target.value)}
            margin="normal"
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Wygeneruj i wyślij do webhooka'}
        </Button>
        <Button variant="outlined" onClick={() => navigate('/janekjot')} sx={{ ml: 2 }}>
          Powrót
        </Button>
      </Box>
    </Box>
  );
};

export default CreatePost;
